import { ButtonOutline, Flex, Spinner } from 'front-end/src/components'
import React, { createContext, useState } from 'react'
import { useParams } from 'react-router-dom'

import { ApolloQueryResult } from '@apollo/client'
import { t, Trans } from '@lingui/macro'

import { linkBuilder, staticLinks } from 'shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import IconButton from '../../../components/common/form/button/IconButton'
import { HeadTitle } from '../../../components/common/head/HeadTitle'
import IconEye from '../../../components/common/icon/IconEye'
import IconFolder from '../../../components/common/icon/IconFolder'
import IconGraph from '../../../components/common/icon/IconGraph'
import IconPeople from '../../../components/common/icon/IconPeople'
import IconSettings from '../../../components/common/icon/IconSettings'
import IconWrench from '../../../components/common/icon/IconWrench'
import Message, {
  MessageTypes
} from '../../../components/common/message/Message'
import PageHeader from '../../../components/common/page/header/PageHeader'
import { Page } from '../../../components/common/page/Page'
import { CertificationAccess } from '../access/CertificationAccess'
import { CertificateTemplate } from '../templates/CertificateTemplate'
import { CertificationsDrawer } from './CertificationsDrawer'
import { CertificationSetup } from './CertificationSetup'
import {
  ManageCertificationPageQuery,
  useManageCertificationPageQuery
} from './ManageCertificationPage.codegen'
import { Certification } from './NewCertificateItemModal'
import {
  StatusDraftGrey,
  StatusOpenGrey
} from 'front-end/src/components/common/status/Status'
import TabsLegacy from 'front-end/src/components/common/tabs/TabsLegacy'

export interface ICertificationsManagePageProps {
  match: { url: string; params: { certificationId: string } }
}

interface CertificationContext {
  certification: Certification
  refetch: () => Promise<ApolloQueryResult<ManageCertificationPageQuery>>
}

export const ManageCertificationPageContext =
  createContext<CertificationContext>(null as unknown as CertificationContext)

export const ManageCertificationPage = ({
  match
}: ICertificationsManagePageProps) => {
  const params = useParams<{
    certificationId: string
  }>()
  const certificationId = parseInt(params.certificationId)

  const [showEditDrawer, setShowEditDrawer] = useState(false)

  const { data, loading, refetch } = useManageCertificationPageQuery({
    variables: {
      id: certificationId
    },
    fetchPolicy: 'cache-first'
  })
  if (loading) {
    return <Spinner />
  }

  if (!data || !data.admin) {
    return null
  }

  const certification = data.admin.certification

  if (!certification) {
    return (
      <Message type={MessageTypes.ERROR}>
        <Trans>Certification doesn't exist</Trans>
      </Message>
    )
  }

  const tabList = [
    {
      caption: t`Setup`,
      icon: <IconWrench />,
      paramName: 'setup',
      getComponent: () => <CertificationSetup />,
      href: match.url
    },
    {
      caption: t`Template`,
      icon: <IconEye />,
      paramName: 'template',
      getComponent: () => <CertificateTemplate />,
      href: `${match.url}/template`
    },
    {
      caption: t`Access`,
      icon: <IconPeople />,
      paramName: 'access',
      getComponent: () => <CertificationAccess />,
      href: `${match.url}/access`
    }
  ]

  return (
    <ManageCertificationPageContext.Provider value={{ certification, refetch }}>
      <HeadTitle title={t`Manage certificates`} />
      <Page className="Certifications Certifications--templates --with-heading">
        <PageHeader
          className="PageHeader"
          title={certification.name}
          titleFollowers={
            <Flex direction="row">
              {certification.locked_at ? (
                <StatusOpenGrey ml="12px" />
              ) : (
                <StatusDraftGrey ml="12px" />
              )}
              <IconButton
                icon={<IconSettings />}
                hint={t`Settings`}
                color="medium"
                onClick={() => {
                  setShowEditDrawer(true)
                }}
              />
            </Flex>
          }
          controls={
            <>
              <ButtonOutline
                onClick={() => navigateTo(staticLinks.certificationsTemplates)}
                leftIcon={<IconFolder />}
              >
                <Trans>Manage templates</Trans>
              </ButtonOutline>
              <ButtonOutline
                onClick={() => {
                  navigateTo(
                    linkBuilder.analyticsCertificationDetail(certification.id)
                  )
                }}
                leftIcon={<IconGraph />}
              >
                <Trans>Analytics</Trans>
              </ButtonOutline>
            </>
          }
          subHeader={certification.description}
        />

        <TabsLegacy tabList={tabList} />

        <CertificationsDrawer
          show={showEditDrawer}
          onHide={() => {
            setShowEditDrawer(false)
          }}
          onUpdate={() => {
            refetch()
          }}
        />
      </Page>
    </ManageCertificationPageContext.Provider>
  )
}
